import * as React from 'react'

import { Button } from '@factorialco/deprecated-design-system'
import type { Form } from 'factorial-form'

import { observer } from 'mobx-react'

import FormRow from 'modules/core/components/FormRow'
import useAsyncAction from 'modules/core/hooks/useAsyncAction'
import { useUnsafeMemo } from 'modules/core/hooks/useMemo'
import i18n from 'modules/core/lib/i18n'

import { type ContractVersions } from 'modules/contracts/store/collections/contractVersions'
import { InputItem } from 'modules/custom_resources/components/CustomFields/CustomFieldsForm/InputItem'
import { coerceValueFromType } from 'modules/custom_resources/components/CustomFields/CustomFieldsForm/coerceValueFromType'
import { getForm } from 'modules/custom_resources/components/CustomFields/CustomFieldsForm/getForm'
import type CustomField from 'modules/custom_resources/store/models/customFields/Field'
import type ResourceField from 'modules/custom_resources/store/models/customFields/ResourceField'
import type { ICustomFieldValue } from 'modules/custom_resources/store/models/customFields/Value'
import CustomFieldValue from 'modules/custom_resources/store/models/customFields/Value'
import type Employee from 'modules/employees/store/models/Employee'

const coerceResult = async (
  saveOperation: Promise<Record<string, unknown>>,
  customField: ResourceField | CustomField
) => {
  const fieldValue = await saveOperation

  return coerceValueFromType(new CustomFieldValue(fieldValue), customField)
}

interface Props {
  employee?: Employee
  fieldValues: CustomFieldValue[]
  resourceFields: ResourceField[]
  createValue: (data: Record<string, unknown>) => Promise<ICustomFieldValue>
  readonly?: boolean
  editableFieldsOnly?: boolean
  contractVersions: ContractVersions
}

const FieldsForm = ({
  fieldValues,
  resourceFields,
  createValue,
  employee,
  readonly,
  editableFieldsOnly,
  contractVersions,
}: Props) => {
  const { forms, isVisible } = useUnsafeMemo(
    () => getForm(resourceFields, fieldValues, contractVersions, employee),
    [resourceFields, fieldValues, employee]
  )

  const { running, performAction: onSubmit } = useAsyncAction(async () =>
    Promise.all(
      forms
        .filter((form) => form.isDirty)
        .map((form) => {
          const customField = resourceFields.find(
            (resource) => +resource.attributes.field_id === +form.get('field_id').value
          )

          const customFieldValue = fieldValues.find(
            (value) => +value.attributes.field_id === +form.get('field_id').value
          )

          if (!customField) {
            return Promise.reject(new Error('custom field wasnt found'))
          }

          if (customFieldValue) {
            return form.handleErrors(() =>
              coerceResult(customFieldValue.save(form.data()), customField)
            )
          }

          return form.handleErrors(() => coerceResult(createValue(form.data()), customField))
        })
    )
  )

  if (!isVisible) return null

  const someFormsAreEditable = forms.some((form) => form.get('editable').value)
  const someFormsAreDirty = forms.some((form) => form.isDirty)
  const showSubmit = !running && !readonly && someFormsAreEditable && someFormsAreDirty

  return (
    <>
      {forms
        .filter((form) => (editableFieldsOnly ? form.get('editable').value : true))
        .map((form: Form, key: number) => {
          const optional = {
            employee,
            readonly,
          }

          return (
            <FormRow key={key}>
              <InputItem form={form} optional={optional} />
            </FormRow>
          )
        })}
      {showSubmit && (
        <FormRow footer>
          <Button onClick={onSubmit}>{i18n.t('common.submit')}</Button>
        </FormRow>
      )}
    </>
  )
  /* eslint-enable react/jsx-indent, react/jsx-curly-newline */
}

export default observer(FieldsForm)
